/**
 * Iter61 — Authorized OT entry dialog.
 *
 * Two modes:
 *   • Single row (default) — pass `daily` row prop. Posts to
 *     POST /api/attendance/daily/{daily_id}/ot-entries.
 *   • Bulk — pass `bulk={ moduleId, defaultDate, employees }`. Posts
 *     to POST /api/attendance/ot-entries/bulk.
 *
 * Multiplier is REQUIRED with no default. Reason is required (min 3
 * chars). Project dropdown includes "— No project —" and, in bulk
 * mode, auto-filters the employee picker to that project's roster.
 * The employee picker uses the same visible-select-all UX pattern as
 * the Officer bulk flow (Iter60.p54.1).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import ProjectPicker from "./ProjectPicker";

function humanHM(minutes) {
    const m = Math.max(0, Number(minutes) || 0);
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return `${h}h ${rem}m`;
}

export default function OTEntryDialog({
    open, onOpenChange, daily = null, bulk = null, onSaved,
}) {
    const isBulk = !!bulk;
    const [hours, setHours] = useState("");
    const [minutes, setMinutes] = useState("");
    const [multiplier, setMultiplier] = useState("");
    const [projectId, setProjectId] = useState("__none__");
    const [projects, setProjects] = useState([]);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    // Bulk mode extras
    const [workDate, setWorkDate] = useState(
        bulk?.defaultDate || new Date().toISOString().slice(0, 10),
    );
    const [empSelection, setEmpSelection] = useState(new Set());

    const moduleId = isBulk ? bulk.moduleId : daily?.module_id;

    useEffect(() => {
        if (!open) return;
        setHours(""); setMinutes(""); setMultiplier("");
        setReason(""); setEmpSelection(new Set());
        setProjectId(daily?.project_id || "__none__");
        setWorkDate(bulk?.defaultDate || new Date().toISOString().slice(0, 10));
    }, [open, daily?.id, bulk?.defaultDate]);

    useEffect(() => {
        if (!open || !moduleId) return;
        api.get("/projects", { params: { module_id: moduleId } })
            .then(({ data }) => setProjects((data?.items || data || []).filter((p) => p.active !== false)))
            .catch(() => setProjects([]));
    }, [open, moduleId]);

    const totalMinutes = useMemo(() => {
        const h = Math.max(0, Number(hours) || 0);
        const m = Math.max(0, Number(minutes) || 0);
        return h * 60 + m;
    }, [hours, minutes]);

    // Bulk mode — project-scoped employee list. Defensive: `bulk.employees`
    // MUST be an array; callers occasionally pass a Map / undefined so we
    // coerce here to avoid crashing the whole page inside useMemo.
    const filteredEmployees = useMemo(() => {
        if (!isBulk) return [];
        const list = Array.isArray(bulk?.employees) ? bulk.employees : [];
        if (projectId === "__none__" || !projectId) return list;
        return list.filter((e) => {
            if (!e.project_ids) return e.project_id === projectId;
            return (e.project_ids || []).includes(projectId);
        });
    }, [isBulk, bulk?.employees, projectId]);

    const toggleEmp = (id) => {
        setEmpSelection((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleAllVisible = () => {
        setEmpSelection((prev) => {
            const allSelected = filteredEmployees.every((e) => prev.has(e.id));
            if (allSelected) return new Set();
            return new Set(filteredEmployees.map((e) => e.id));
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (totalMinutes <= 0) return toast.error("Enter OT hours or minutes (>0).");
        if (!multiplier || Number(multiplier) <= 0) return toast.error("Multiplier is required (e.g., 1.5).");
        if ((reason || "").trim().length < 3) return toast.error("Reason must be at least 3 characters.");
        setSaving(true);
        try {
            if (isBulk) {
                if (empSelection.size === 0) return toast.error("Select at least one employee.");
                const body = {
                    module_id: moduleId,
                    work_date: workDate,
                    employee_ids: Array.from(empSelection),
                    minutes: totalMinutes,
                    multiplier: Number(multiplier),
                    project_id: projectId === "__none__" ? null : projectId,
                    reason: reason.trim(),
                };
                const { data } = await api.post("/attendance/ot-entries/bulk", body);
                toast.success(`OT authorized for ${data.created_count} employee(s)`);
            } else {
                const body = {
                    minutes: totalMinutes,
                    multiplier: Number(multiplier),
                    project_id: projectId === "__none__" ? null : projectId,
                    reason: reason.trim(),
                };
                await api.post(`/attendance/daily/${daily.id}/ot-entries`, body);
                toast.success("OT entry added");
            }
            onOpenChange(false);
            await onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[85vh] p-0 flex flex-col overflow-hidden"
                data-testid={isBulk ? "ot-bulk-dialog" : "ot-single-dialog"}
            >
                <DialogHeader className="p-6 pb-3 border-b flex-shrink-0" style={{ borderColor: "var(--asl-border)" }}>
                    <DialogTitle>{isBulk ? "Bulk add OT hours" : "Add OT hours"}</DialogTitle>
                    <div className="text-xs text-[color:var(--asl-muted)] mt-1">
                        {isBulk
                            ? "Manager-authorized OT applied to multiple employees on a single date."
                            : (daily
                                ? <>Row: <span className="asl-navy-text font-medium">{daily.employee_name || daily.employee_id}</span> · {daily.work_date}</>
                                : null)}
                    </div>
                    {/* Iter82 — Inline detected-OT hint. When the row has
                        detected minutes (past shift end) show them so the
                        manager can decide whether the authorized value
                        should match, exceed, or fall below detected. */}
                    {!isBulk && daily && Number(daily.detected_ot_minutes || 0) > 0 && (
                        <div
                            className="mt-2 flex items-center gap-1.5 text-[11px] rounded-md px-2 py-1"
                            style={{
                                background: "rgba(107,114,128,0.10)",
                                color: "rgb(75,85,99)",
                                border: "1px solid rgba(107,114,128,0.22)",
                            }}
                            data-testid="ot-dialog-detected-hint"
                        >
                            <span className="italic">Detected: {humanHM(Number(daily.detected_ot_minutes || 0))} from late clock-out (informational — not paid unless authorized here).</span>
                        </div>
                    )}
                </DialogHeader>

                <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {isBulk && (
                        <div>
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={workDate}
                                onChange={(e) => setWorkDate(e.target.value)}
                                data-testid="ot-bulk-date"
                            />
                        </div>
                    )}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Hours</Label>
                            <Input
                                type="number" min="0" step="1"
                                placeholder="0"
                                value={hours}
                                onChange={(e) => setHours(e.target.value)}
                                data-testid="ot-hours-input"
                            />
                        </div>
                        <div>
                            <Label>Minutes</Label>
                            <Input
                                type="number" min="0" max="59" step="1"
                                placeholder="0"
                                value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                data-testid="ot-minutes-input"
                            />
                        </div>
                        <div>
                            <Label>Multiplier <span className="asl-crimson-text">*</span></Label>
                            <Input
                                type="number" step="0.05" min="0.05"
                                placeholder="e.g., 1.5"
                                value={multiplier}
                                onChange={(e) => setMultiplier(e.target.value)}
                                data-testid="ot-multiplier-input"
                            />
                        </div>
                    </div>
                    <div className="text-xs text-[color:var(--asl-muted)]" data-testid="ot-total-preview">
                        Total: <span className="asl-navy-text font-medium">{humanHM(totalMinutes)}</span>{multiplier && Number(multiplier) > 0 ? ` × ${Number(multiplier).toFixed(2)}` : ""}
                    </div>
                    <div>
                        <Label>Project</Label>
                        {/* Iter67 F1 — Universal ProjectPicker.
                            OTEntryDialog historically used "__none__"
                            sentinel; keep it in local state for the
                            downstream project_id nullification logic. */}
                        <ProjectPicker
                            projects={projects}
                            value={projectId === "__none__" ? "" : projectId}
                            onValueChange={(v) => setProjectId(v || "__none__")}
                            allowNone
                            testid="ot-project-select"
                        />
                    </div>
                    <div>
                        <Label>Reason <span className="asl-crimson-text">*</span></Label>
                        <Textarea
                            rows={2}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Why is this OT authorized? (min. 3 chars)"
                            data-testid="ot-reason-input"
                        />
                    </div>

                    {isBulk && (
                        <div className="border rounded p-3" style={{ borderColor: "var(--asl-border)" }}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-semibold asl-navy-text">
                                    Employees ({filteredEmployees.length} shown, {empSelection.size} selected)
                                </div>
                                <button
                                    type="button"
                                    onClick={toggleAllVisible}
                                    className="text-xs asl-navy-text underline"
                                    data-testid="ot-bulk-select-visible"
                                >
                                    {filteredEmployees.every((e) => empSelection.has(e.id)) ? "Clear" : "Select all visible"}
                                </button>
                            </div>
                            <div className="max-h-64 overflow-y-auto divide-y" data-testid="ot-bulk-emp-list">
                                {filteredEmployees.map((e) => (
                                    <label
                                        key={e.id}
                                        className="flex items-center gap-2 py-1.5 text-xs cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={empSelection.has(e.id)}
                                            onCheckedChange={() => toggleEmp(e.id)}
                                            data-testid={`ot-bulk-emp-${e.id}`}
                                        />
                                        <span className="asl-navy-text font-medium">{e.full_name}</span>
                                        {e.module_employee_id && (
                                            <span className="text-[10px] mono text-[color:var(--asl-muted)]">
                                                · {e.module_employee_id}
                                            </span>
                                        )}
                                    </label>
                                ))}
                                {filteredEmployees.length === 0 && (
                                    <div className="text-xs text-[color:var(--asl-muted)] py-3 text-center">
                                        No employees match the current project filter.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </form>

                <DialogFooter className="p-4 border-t flex-shrink-0" style={{ borderColor: "var(--asl-border)" }}>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ot-dialog-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="asl-navy-bg text-white" data-testid="ot-dialog-save">
                        {saving ? "Saving…" : "Authorize OT"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
